import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { useForm } from "react-hook-form";
import { object, string } from "zod";

import { Input, Label, Button } from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";
import type { NapiUser } from "@app/types/napi-types";

import type { SubmitHandler } from "react-hook-form";

import {
  clickedUpdatePassword,
  updatedPassword
} from "../../../../../tracking/account-tracking";
import { getErrorText } from "../../../utils";

type OwnProps = {
  onSuccess: () => void;
};
type ReduxProps = {
  user: NapiUser;
};

export const PasswordSection: React.FC<OwnProps & ReduxProps> = ({
  user,
  onSuccess
}) => {
  const { tracker, translator } = useAppContext();
  const methods = useForm({
    defaultValues: { current_password: "", new_password: "" },
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: zodResolver(
      object({
        current_password: string({
          message: getErrorText(translator, "password.required")
        }),
        new_password: string({
          message: getErrorText(translator, "password.required")
        }).min(8, getErrorText(translator, "password.invalid"))
      }).optional()
    )
  });
  const {
    register,
    reset,
    handleSubmit,
    setError,
    formState: { touchedFields, errors, isValid, isSubmitting, isDirty },
    getValues
  } = methods;

  const onSubmit: SubmitHandler<any> = async values => {
    tracker.track(clickedUpdatePassword());
    try {
      await axios.put<NapiUser>("/auth/update-password", {
        user_id: user.id,
        current_password: values.current_password,
        new_password: values.new_password
      });
      reset();
      tracker.track(updatedPassword());
      onSuccess();
    } catch (err) {
      setError(
        "current_password",
        {
          message: getErrorText(translator, "password.invalid")
        },
        { shouldFocus: true }
      );
    }
  };

  return (
    <div className="flex flex-col gap-200">
      <div className="flex flex-col gap-100">
        <Label size="lg" fontWeight="bold" as="span">
          {translator.t("!accounts.change-password.title")}
        </Label>
        <Label size="md" className="text-color-secondary" as="span">
          {translator.t("!accounts.change-password.description")}
        </Label>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-150">
          <Input
            size="md"
            type="password"
            isDisabled={user.signin_method !== "email"}
            labelText={translator.t(
              "!accounts.change-password.current-password"
            )}
            isInvalid={
              !!touchedFields.current_password && !!errors.current_password
            }
            errorText={errors.current_password?.message}
            className="w-full"
            data-testid="current_password"
            {...register("current_password")}
          />
          <Input
            size="md"
            type="password"
            isDisabled={user.signin_method !== "email"}
            labelText={translator.t("!accounts.change-password.new-password")}
            isInvalid={
              !!touchedFields.new_password &&
              !!getValues("new_password") &&
              !!errors.new_password
            }
            errorText={errors.new_password?.message}
            className="w-full"
            data-testid="new_password"
            {...register("new_password")}
          />
          {!!isDirty && (
            <Button
              id="submit"
              isSubmit
              appearance="primary"
              isDisabled={isSubmitting || !isValid}
              variant="contained"
              size="lg"
              data-testid="submit"
              className="w-full"
            >
              {translator.t("!button.save")}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};
